// Frequently Asked Questions — The Uncanny House
// Structure: [{ id, title, questions: [{ q, a }] }]
// `a` supports simple paragraph splits via \n\n and bulleted lists via lines beginning with "• "

export const FAQ_SECTIONS = [
  {
    id: "about",
    title: "About The House",
    questions: [
      {
        q: "What is The Uncanny House?",
        a: "The Uncanny House is a mobile coffee house built around thoughtfully crafted drinks, limited dessert drops, and unforgettable moments. Every detail is intentional. Every visit is crafted for obsession.",
      },
      {
        q: "Is The Uncanny House a coffee shop?",
        a: "Not exactly.\n\nWe don't run a coffee shop. We run a house.\n\nThe Uncanny House travels throughout the Central Valley, bringing carefully curated drinks and desserts to pop-ups, community events, and special locations.",
      },
      {
        q: "Who is Pumabilt?",
        a: "Pumabilt is the face of The Uncanny House.\n\nInspired by the iconic Peterbilt 359, she was brought back to life as a custom half-scale truck built exclusively for The House. More than transportation, Pumabilt is part of the experience.\n\n*Built for beauty. Designed for flow. Born from passion.*",
      },
      {
        q: "Why is everything called \u201cThe House?\u201d",
        a: "Because we believe coffee is about more than what is in the cup.\n\nThe House is our philosophy. It's a place where craftsmanship, hospitality, and thoughtful details come together to create an experience worth remembering.",
      },
    ],
  },
  {
    id: "locations",
    title: "Locations & Pop-Ups",
    questions: [
      {
        q: "Where are you today?",
        a: "Join House Alerts to receive text messages whenever Pumabilt opens her doors.\n\nWe announce locations, special events, and limited drops through House Alerts before anywhere else.",
      },
      {
        q: "Do you have a permanent location?",
        a: "Not yet.\n\nFor now, The House travels.\n\nEvery stop is intentional.",
      },
      {
        q: "How do I know where Pumabilt will be?",
        a: "The best way is to join House Alerts.\n\nSubscribers receive:\n• Weekly locations\n• Surprise appearances\n• New drink releases\n• Dessert Drops\n• Special events\n• Pickup announcements",
      },
    ],
  },
  {
    id: "ordering",
    title: "Ordering",
    questions: [
      {
        q: "Can I order ahead?",
        a: "Yes.\n\nOnline ordering for pickup is available through Toast during participating pop-ups and scheduled pickup days.",
      },
      {
        q: "Do you deliver?",
        a: "Not at this time.\n\nAll orders are pickup only.",
      },
      {
        q: "Can I reserve Limited Drops?",
        a: "Some Limited Drops and dessert releases will be available for preorder.\n\nWhen they're gone, they're gone.",
      },
    ],
  },
  {
    id: "drinks",
    title: "Drinks",
    questions: [
      {
        q: "What coffee do you use?",
        a: "We serve carefully selected coffee chosen to meet The House standards.\n\nEvery drink is built around quality, balance, and consistency.",
      },
      {
        q: "Do you offer decaf?",
        a: "Yes.\n\nAvailability may vary by event.",
      },
      {
        q: "Do you offer dairy alternatives?",
        a: "Yes.\n\nWe typically offer oat milk and almond milk.\n\nAvailability may vary.",
      },
      {
        q: "Are your drinks very sweet?",
        a: "Many of our drinks are intentionally balanced rather than overly sweet.\n\nIf you'd like your drink sweeter or lighter, just let us know.",
      },
      {
        q: "What is a Limited Drop?",
        a: "Limited Drops are seasonal drinks and desserts produced in intentionally small quantities.\n\nSome return.\n\nMany never do.",
      },
    ],
  },
  {
    id: "desserts",
    title: "Cinnamon Rolls & Desserts",
    questions: [
      {
        q: "Are your cinnamon rolls made fresh?",
        a: "Yes.\n\nEvery batch is prepared with premium ingredients and made in small batches.\n\nQuality always comes before quantity.",
      },
      {
        q: "Do flavors change?",
        a: "Absolutely.\n\nRotating flavors are part of The House experience.\n\nThere will always be something new worth discovering.",
      },
      {
        q: "Can I preorder cinnamon rolls?",
        a: "Yes.\n\nPreorders will be available for select drops when announced.",
      },
      {
        q: "Do you make custom orders?",
        a: "Yes, when The House has room for them.\n\nBecause everything is prepared in small batches, our ability to accept custom orders depends on our production schedule, ingredient availability, and upcoming House Drops. If we can make it happen without compromising quality, we'd be delighted to do so.",
      },
    ],
  },
  {
    id: "house-alerts",
    title: "House Alerts",
    questions: [
      {
        q: "What are House Alerts?",
        a: "House Alerts are text notifications that let you know:\n• Where Pumabilt is parked\n• New drink drops\n• Dessert releases\n• Pickup availability\n• Surprise appearances\n• Special events",
      },
      {
        q: "How often will you text me?",
        a: "Only when there's something worth sharing.\n\nWe respect your inbox.",
      },
      {
        q: "Can I unsubscribe?",
        a: "Absolutely.\n\nReply STOP at any time.",
      },
    ],
  },
  {
    id: "chime-in",
    title: "Chime In",
    questions: [
      {
        q: "What is Chime In?",
        a: "Chime In is where you help shape The House.\n\nHave an idea for a new drink? A cinnamon roll flavor? A dessert you'd love to see? We'd love to hear it.\n\nSome of our future Limited Drops may begin with a suggestion from a guest.",
      },
      {
        q: "Can I suggest a drink or dessert?",
        a: "Absolutely.\n\nVisit Chime In to submit ideas for:\n• Coffee drinks\n• Matcha creations\n• Cold brews\n• Cinnamon roll flavors\n• Dessert Drops\n• Heritage Collection ideas\n• Pop-up locations\n• Events and collaborations",
      },
      {
        q: "Will every suggestion make the menu?",
        a: "Not every suggestion becomes a House Drop, but every submission is appreciated and thoughtfully considered.\n\nIf it fits The House, you just might see it on the menu.",
      },
      {
        q: "Can I vote on future menu items?",
        a: "Yes.\n\nFrom time to time we'll invite the House to vote on future Limited Drops through Chime In.",
      },
    ],
  },
  {
    id: "founding-stewards",
    title: "Founding Stewards",
    questions: [
      {
        q: "What are Founding Stewards?",
        a: "Founding Stewards are the people helping lay the foundation of The Uncanny House before the doors officially open.\n\nStewardship is limited to 111 Founding Stewards and will never be offered again once all Stewardships have been claimed.",
      },
      {
        q: "Is Founding Stewardship still available?",
        a: "If Stewardships remain, you'll find them on our website.\n\nOnce all 111 Stewardships have been claimed, Founding Stewardship will close permanently.",
      },
    ],
  },
  {
    id: "merchandise",
    title: "Merchandise",
    questions: [
      {
        q: "Can I buy merchandise online?",
        a: "Yes.\n\nSelect merchandise and collectibles will be available while supplies last.",
      },
    ],
  },
  {
    id: "events",
    title: "Events",
    questions: [
      {
        q: "Can I book Pumabilt for a private event?",
        a: "Private bookings are coming soon.\n\nVisit our Book The House page to tell us about your event.",
      },
    ],
  },
  {
    id: "policies",
    title: "Policies",
    questions: [
      {
        q: "What forms of payment do you accept?",
        a: "We accept all major credit cards, Apple Pay, Google Pay, and other contactless payment methods.",
      },
      {
        q: "Do you accept cash?",
        a: "When available, yes.\n\nCard payments are encouraged.",
      },
      {
        q: "What if I have food allergies?",
        a: "Please let us know before ordering.\n\nWhile we take great care in preparation, our products are prepared in environments where common allergens may be present.",
      },
      {
        q: "Are your cups and packaging recyclable?",
        a: "Whenever possible, we use thoughtfully selected packaging designed to minimize environmental impact while preserving the experience.",
      },
    ],
  },
  {
    id: "selling-out",
    title: "Selling Out",
    questions: [
      {
        q: "What happens if you sell out?",
        a: "When a House Drop is gone, it's gone.\n\nMany of our drinks and desserts are intentionally produced in limited quantities to ensure every guest receives the quality they expect.",
      },
      {
        q: "Why do some menu items disappear?",
        a: "The House is built around seasonal creations and Limited Drops.\n\nSome favorites return.\n\nOthers become part of The House's story.",
      },
    ],
  },
  {
    id: "gift-cards",
    title: "Gift Cards",
    questions: [
      {
        q: "Do you offer gift cards?",
        a: "Yes.\n\nGift cards are available online and can be redeemed at participating pop-ups and pickup events.",
      },
    ],
  },
  {
    id: "philosophy",
    title: "Our Philosophy",
    questions: [
      {
        q: "Why \u201cCrafted for Obsession?\u201d",
        a: "Because we believe the smallest details create the biggest memories.\n\nFrom the crack of a pop top to the first sip of coffee, every drink, dessert, and interaction is intentionally designed to be remembered.\n\nWe don't chase perfection for perfection's sake.\n\nWe pursue the kind of quality that keeps people coming back to see what's next.",
      },
    ],
  },
];
